// Disponibilité des modèles (contrat C0 v1.6). GET /api/models expose, par modèle :
//   disponible : true | false | null (null = non sondé, ex. Claude) ;
//   indisponible_raison : raison courte en français, seulement quand disponible vaut false.
// Un modèle indisponible reste affiché mais ne peut pas être choisi (badge « Indisponible »,
// raison en infobulle) ; la liste est relue toutes les 60 s quand l'onglet est visible et au
// retour sur l'onglet.
// Modèle par défaut dynamique (décision du 6 octobre 2026, « Qwen par défaut tant qu'il est
// disponible ») : defaultId de /api/models est le défaut effectif du serveur. Sans choix
// explicite, la sélection le suit à chaque relecture ; un choix explicite ne vaut que pour
// la session de l'onglet (sessionStorage) et cède la place au défaut du serveur si son
// modèle devient indisponible ; chaque bascule qui ne vient pas de l'utilisateur est annoncée.
// Fonctions pures, sans React ni accès direct à window : stockage injecté (tests node).

export const UNAVAILABLE_LABEL = 'Indisponible';
export const MODELS_REFRESH_MS = 60 * 1000;
// Retour sur l'onglet (focus puis visibilité, souvent ensemble) : une seule lecture
export const MODELS_REFRESH_MIN_GAP_MS = 5 * 1000;

/** Indisponible seulement si le serveur l'a constaté (`disponible === false`). */
export function isModelUnavailable(model) {
  return model?.disponible === false;
}

/** Infobulle d'un modèle indisponible : raison du serveur si elle est fournie. */
export function unavailableTooltip(model) {
  const raison = typeof model?.indisponible_raison === 'string' ? model.indisponible_raison.trim().slice(0, 120) : '';
  return raison ? `Modèle indisponible : ${raison}` : 'Modèle momentanément indisponible';
}

/**
 * Options du menu : {id, label, description, selected, disabled, tooltip}.
 * Le modèle sélectionné reste coché même s'il devient indisponible.
 */
export function modelOptions(models, selectedId) {
  return (Array.isArray(models) ? models : []).map((model) => {
    const disabled = isModelUnavailable(model);
    return {
      id: model.id,
      label: model.label || model.id,
      description: model.description || '',
      selected: model.id === selectedId,
      disabled,
      tooltip: disabled ? unavailableTooltip(model) : null,
    };
  });
}

/** Option activable suivante dans le sens `step` (+1 / −1), en boucle ; −1 si aucune. */
export function nextEnabledIndex(options, from, step) {
  const n = Array.isArray(options) ? options.length : 0;
  if (n === 0 || (step !== 1 && step !== -1)) return -1;
  for (let k = 1; k <= n; k += 1) {
    const i = (((from + step * k) % n) + n) % n;
    if (!options[i]?.disabled) return i;
  }
  return -1;
}

/** Focus à l'ouverture du menu au clavier : la sélection si activable, sinon la première activable. */
export function initialFocusIndex(options) {
  const selected = (options ?? []).findIndex((o) => o.selected && !o.disabled);
  return selected >= 0 ? selected : nextEnabledIndex(options, -1, 1);
}

// ── Choix explicite de la session ────────────────────────────────────────

// sessionStorage : choix explicite de l'utilisateur, pour la session de l'onglet seulement
export const MODEL_CHOICE_KEY = 'assistant-flotte:modelChoice';
// localStorage : ancien choix mémorisé sans limite de durée (avant le 6 octobre 2026), supprimé
export const LEGACY_MODEL_KEY = 'assistant-flotte:modelId';

/** Choix explicite mémorisé, ou null (aucun choix, stockage bloqué ou illisible). */
export function readModelChoice(storage) {
  try {
    const value = storage?.getItem(MODEL_CHOICE_KEY);
    return typeof value === 'string' && value ? value : null;
  } catch {
    return null;
  }
}

/** Mémorise (ou efface, avec null) le choix explicite ; stockage bloqué : sans effet. */
export function writeModelChoice(storage, modelId) {
  try {
    if (modelId) storage?.setItem(MODEL_CHOICE_KEY, modelId);
    else storage?.removeItem(MODEL_CHOICE_KEY);
  } catch {
    /* stockage indisponible : le choix vaut jusqu'au rechargement de la page */
  }
}

/** Migration : l'ancien choix persistant est supprimé, jamais repris (le défaut du serveur prime). */
export function dropLegacyModelChoice(storage) {
  try {
    storage?.removeItem(LEGACY_MODEL_KEY);
  } catch {
    /* stockage indisponible : rien à supprimer */
  }
}

// ── Sélection après une lecture de /api/models ──────────────────────────

/**
 * Modèle par défaut à sélectionner : defaultId du serveur s'il est listé et pas
 * indisponible, sinon le premier modèle qui ne l'est pas, sinon defaultId s'il est listé,
 * sinon le premier ; null si la liste est vide.
 */
export function fallbackModelId(models, defaultId = null) {
  const list = Array.isArray(models) ? models : [];
  const byDefault = defaultId ? list.find((m) => m?.id === defaultId) : null;
  if (byDefault && !isModelUnavailable(byDefault)) return byDefault.id;
  const firstUp = list.find((m) => m && !isModelUnavailable(m));
  if (firstUp) return firstUp.id;
  return byDefault?.id ?? list[0]?.id ?? null;
}

/**
 * Sélection retenue : le choix explicite tant que son modèle est listé et pas indisponible,
 * sinon le modèle par défaut du serveur (fallbackModelId).
 * @returns {{id: string|null, explicit: boolean, dropped: boolean}} dropped : le choix
 *   explicite n'est plus retenu (modèle retiré ou indisponible) et doit être oublié.
 */
export function resolveSelection({ models, defaultId = null, explicitId = null }) {
  const list = Array.isArray(models) ? models : [];
  const chosen = explicitId ? list.find((m) => m?.id === explicitId) : null;
  if (chosen && !isModelUnavailable(chosen)) return { id: chosen.id, explicit: true, dropped: false };
  // Liste vide (serveur sans modèle) : rien ne permet de juger le choix, il est gardé
  return { id: fallbackModelId(list, defaultId), explicit: false, dropped: Boolean(explicitId) && list.length > 0 };
}

const labelIn = (list, id) => {
  const model = (Array.isArray(list) ? list : []).find((m) => m?.id === id);
  return model ? model.label || model.id : null;
};

/**
 * Annonce d'un changement de sélection qui ne vient pas de l'utilisateur, ou null.
 * previousId : sélection avant cette lecture (null au premier chargement) ;
 * previousModels / models : listes avant et après ; lostChoiceId : choix explicite oublié
 * à cette lecture. Premier chargement : seule la perte d'un choix explicite devenu
 * indisponible est annoncée.
 * @returns {{text: string, tone: 'warning'|'success'|'info'}|null}
 */
export function selectionNotice({ previousId = null, nextId = null, previousModels = [], models = [], lostChoiceId = null }) {
  if (!nextId) return null;
  const next = labelIn(models, nextId) ?? nextId;
  if (!previousId) {
    const lost = lostChoiceId ? (Array.isArray(models) ? models : []).find((m) => m?.id === lostChoiceId) : null;
    if (!lost || !isModelUnavailable(lost) || lostChoiceId === nextId) return null;
    return { text: `« ${lost.label || lost.id} » est momentanément indisponible : « ${next} » est sélectionné.`, tone: 'warning' };
  }
  if (previousId === nextId) return null;
  const before = (Array.isArray(models) ? models : []).find((m) => m?.id === previousId);
  const previous = labelIn(models, previousId) ?? labelIn(previousModels, previousId) ?? previousId;
  if (!before) return { text: `« ${previous} » n'est plus proposé : « ${next} » est sélectionné.`, tone: 'warning' };
  if (isModelUnavailable(before)) return { text: `« ${previous} » est momentanément indisponible : « ${next} » est sélectionné.`, tone: 'warning' };
  const nextBefore = (Array.isArray(previousModels) ? previousModels : []).find((m) => m?.id === nextId);
  if (isModelUnavailable(nextBefore)) return { text: `« ${next} » est de nouveau disponible et sélectionné.`, tone: 'success' };
  return { text: `Modèle par défaut : « ${next} » est sélectionné.`, tone: 'info' };
}

/** Relire la liste au retour sur l'onglet ? Seulement s'il est visible et sans lecture récente. */
export function shouldRefreshOnReturn({ visible, lastFetchAt, now, minGapMs = MODELS_REFRESH_MIN_GAP_MS }) {
  return Boolean(visible) && (!lastFetchAt || now - lastFetchAt >= minGapMs);
}

/** Même liste (contenu et ordre) : l'état React n'est pas remplacé, aucun rendu inutile. */
export function sameModels(a, b) {
  return JSON.stringify(a ?? []) === JSON.stringify(b ?? []);
}
